import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Wallet,
  Receipt,
  Building2,
  ShoppingCart,
  Package,
  MessageCircle,
  Settings,
  HelpCircle,
  Upload,
  GitMerge,
  Layers,
  Printer,
  ShoppingBag,
  ClipboardList,
  FileText,
  PlusCircle,
  Home,
  ChevronDown,
  Menu,
  Truck,
  X,
  ListChecks,
} from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_FARMS: NavItem[] = [
  { to: '/farms',             label: 'Dashboard',         icon: Home },
  { to: '/farms/marketplace', label: 'Marketplace',       icon: ShoppingBag },
  { to: '/farms/cadastrar',   label: 'Cadastrar farm',    icon: PlusCircle },
  { to: '/farms/minhas',      label: 'Minhas farms',      icon: Printer },
  { to: '/farms/recebidos',   label: 'Pedidos recebidos', icon: FileText },
  { to: '/farms/pedidos',     label: 'Meus pedidos',      icon: ClipboardList },
  { to: '/farms/conversas',   label: 'Conversas',         icon: MessageCircle },
];

const NAV_FINANCIAL: NavItem[] = [
  { to: '/tarefas',                     label: 'Tarefas',       icon: ListChecks },
  { to: '/financeiro',                  label: 'Visão geral',   icon: LayoutDashboard },
  { to: '/financeiro/contas',           label: 'Contas',        icon: Wallet },
  { to: '/financeiro/pagar',            label: 'A pagar',       icon: Receipt },
  { to: '/financeiro/receber',          label: 'A receber',     icon: Building2 },
  { to: '/financeiro/importar',         label: 'Importar CSV',  icon: Upload },
  { to: '/financeiro/dre',              label: 'DRE',           icon: FileText },
  { to: '/financeiro/conciliacao',      label: 'Conciliação',   icon: GitMerge },
  { to: '/configuracoes/fornecedores',  label: 'Fornecedores',  icon: Truck },
  { to: '/configuracoes/canais',        label: 'Canais',        icon: Layers },
];

const NAV_LOGISTICS: NavItem[] = [
  { to: '/logistica',             label: 'Pedidos E-commerce',  icon: ShoppingCart },
  { to: '/logistica/estoque',     label: 'Estoque',     icon: Package },
  { to: '/logistica/atendimento', label: 'Atendimento', icon: MessageCircle },
];

const navCls = (active: boolean, highlight?: boolean) =>
  cn(
    'flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors',
    active
      ? highlight
        ? 'bg-[#14a655]/10 text-[#14a655]'
        : 'bg-brand-50 text-brand-800'
      : 'text-ink-muted hover:text-ink hover:bg-surface-subtle'
  );

function Section({
  label, items, highlight, defaultOpen = true,
}: {
  label: string;
  items: NavItem[];
  highlight?: boolean;
  defaultOpen?: boolean;
}) {
  const location = useLocation();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2.5 py-1 mb-0.5"
      >
        <span className={cn(
          'text-[10px] font-semibold uppercase tracking-widest',
          highlight ? 'text-[#14a655]' : 'text-ink-soft'
        )}>
          {label}
        </span>
        <ChevronDown className={cn(
          'w-3 h-3 transition-transform',
          highlight ? 'text-[#14a655]/60' : 'text-ink-soft',
          !open && '-rotate-90'
        )} />
      </button>

      {open && (
        <div className="space-y-0.5">
          {items.map((item) => {
            const isActive = item.to === '/farms'
              ? location.pathname === '/farms'
              : item.to === '/financeiro'
              ? location.pathname === '/financeiro'
              : item.to === '/logistica'
              ? location.pathname === '/logistica'
              : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end
                className={() => navCls(isActive, highlight)}
              >
                <item.icon className="w-3.5 h-3.5 shrink-0" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SidebarContent() {
  return (
    <>
      <nav className="flex-1 px-2 py-3 space-y-2 overflow-y-auto min-h-0">
        <Section label="MYND Farms"      items={NAV_FARMS}      highlight defaultOpen />
        <Section label="Ferramentas BPO" items={NAV_FINANCIAL}  defaultOpen />
        <Section label="E-commerce"      items={NAV_LOGISTICS}  defaultOpen />
      </nav>

      <div className="px-2 py-3 border-t border-border space-y-0.5 shrink-0">
        <NavLink
          to="/configuracoes"
          className={({ isActive }) => navCls(isActive)}
        >
          <Settings className="w-3.5 h-3.5 shrink-0" />
          Configurações
        </NavLink>
        <NavLink
          to="/ajuda/erros"
          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] font-medium text-ink-muted hover:text-ink hover:bg-surface-subtle transition-colors"
        >
          <HelpCircle className="w-3.5 h-3.5 shrink-0" />
          Suporte
        </NavLink>
      </div>
    </>
  );
}

// ─── Mobile drawer ─────────────────────────────────────────────────────────────

function MobileSidebar() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-40 btn-secondary !p-2"
        aria-label="Menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      <aside className={cn(
        'lg:hidden fixed top-0 left-0 h-full z-50 w-60 flex flex-col border-r border-border bg-surface-raised transition-transform duration-250',
        open ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="px-4 h-14 flex items-center justify-between border-b border-border shrink-0">
          <Logo size={22} />
          <button onClick={() => setOpen(false)} className="btn-ghost !p-1.5">
            <X className="w-4 h-4" />
          </button>
        </div>
        <SidebarContent />
      </aside>
    </>
  );
}

// ─── Desktop sidebar ──────────────────────────────────────────────────────────

export function Sidebar() {
  return (
    <>
      {/* Desktop */}
      <aside className="hidden lg:flex w-56 shrink-0 flex-col border-r border-border bg-surface-raised">
        <div className="px-4 h-16 flex items-center border-b border-border shrink-0">
          <Logo size={26} />
        </div>
        <SidebarContent />
      </aside>

      {/* Mobile */}
      <MobileSidebar />
    </>
  );
}
